import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `UGX ${Number(v || 0).toLocaleString()}`;
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const th = 'px-2 py-1.5 text-left text-[10px] font-semibold uppercase bg-muted border-b';
const td = 'px-2 py-1.5 border-b text-xs';

// Live staff-compensation view of Finance → Expenses (personnel group). No manual entry here.
export const CompensationTrackerTab = ({ onOpenPayroll }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/payroll/compensation-tracker?month=${month}&year=${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [month, year]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>;
  const label = (s) => (s || 'other').replace(/_/g, ' ');
  const go = data.general_overview;

  return (
    <div className="space-y-4" data-testid="compensation-tracker">
      <div className="flex items-center gap-3">
        <Activity className="h-4 w-4 text-primary" />
        <div className="flex-1">
          <h3 className="font-heading font-semibold">Staff Compensation Tracker</h3>
          <p className="text-xs text-muted-foreground">Live view of Finance → Expenses (Personnel). Record once in Finance or Payroll — it appears here automatically.</p>
        </div>
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="w-36" data-testid="tracker-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-24" data-testid="tracker-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[year - 2, year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <Card data-testid="comp-overview">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Monthly Staff Compensation Overview — {MONTHS[month - 1]} {year}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead><tr><th className={th}>Item</th><th className={`${th} text-right`}>Amount</th></tr></thead>
              <tbody>
                {Object.entries(data.by_subcategory).map(([k, v]) => (
                  <tr key={k}><td className={`${td} capitalize`}>{label(k)}</td><td className={`${td} text-right tabular-nums`}>{fmt(v)}</td></tr>
                ))}
                {Object.keys(data.by_subcategory).length === 0 && <tr><td colSpan={2} className="text-center py-4 text-muted-foreground text-xs">No staff-compensation expenses this month</td></tr>}
                <tr className="bg-muted/60 font-semibold"><td className={td}>TOTAL STAFF COMPENSATION EXPENSE</td><td className={`${td} text-right tabular-nums`} data-testid="comp-total">{fmt(data.total_staff_compensation)}</td></tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
        <Card data-testid="general-overview">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">{MONTHS[month - 1]} {year} — General Financial Overview</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full"><tbody>
              {[['Total Income', go.total_income], ['Total Expenses', go.total_expenses], ['Staff Compensation', go.staff_compensation], ['Other Expenses', go.other_expenses]].map(([l, v]) => (
                <tr key={l}><td className={td}>{l}</td><td className={`${td} text-right tabular-nums`}>{fmt(v)}</td></tr>
              ))}
              <tr className={`font-semibold ${go.remaining_balance >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <td className={td}>Remaining Balance</td><td className={`${td} text-right tabular-nums`}>{fmt(go.remaining_balance)}</td></tr>
            </tbody></table>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="comp-breakdown">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Staff Compensation Expenses — {MONTHS[month - 1]} {year}</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full min-w-[720px]">
            <thead><tr>{['Date', 'Expense Category', 'Description', 'Amount', 'Reference', 'Status', 'Source'].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.breakdown.map((r) => (
                <tr key={r.id} className={`hover:bg-muted/40 ${r.payroll_id ? 'cursor-pointer' : ''}`}
                  onClick={() => r.payroll_id && onOpenPayroll?.(month, year)} data-testid={`comp-row-${r.id}`}>
                  <td className={td}>{r.date}</td>
                  <td className={`${td} capitalize`}>{label(r.subcategory)}</td>
                  <td className={td}>{r.description || '—'}</td>
                  <td className={`${td} tabular-nums`}>{fmt(r.amount)}</td>
                  <td className={`${td} font-mono text-[10px]`}>{r.payroll_id ? `PAY-${year}-${String(month).padStart(2, '0')}` : String(r.reference).slice(0, 12)}</td>
                  <td className={`${td} capitalize`}>{r.status}</td>
                  <td className={td}>{r.source}</td>
                </tr>
              ))}
              {data.breakdown.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground text-xs">Nothing recorded this month</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card data-testid="category-summary">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Expense Category Summary — {MONTHS[month - 1]} vs Year-to-Date</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full">
            <thead><tr><th className={th}>Expense Category</th><th className={`${th} text-right`}>{MONTHS[month - 1]}</th><th className={`${th} text-right`}>Year-to-Date</th></tr></thead>
            <tbody>
              {data.category_summary.map((s) => (
                <tr key={s.subcategory}><td className={`${td} capitalize`}>{label(s.subcategory)}</td>
                  <td className={`${td} text-right tabular-nums`}>{fmt(s.month)}</td>
                  <td className={`${td} text-right tabular-nums`}>{fmt(s.ytd)}</td></tr>
              ))}
              <tr className="bg-muted/60 font-semibold"><td className={td}>TOTAL</td>
                <td className={`${td} text-right tabular-nums`}>{fmt(data.category_summary.reduce((a, s) => a + s.month, 0))}</td>
                <td className={`${td} text-right tabular-nums`}>{fmt(data.category_summary.reduce((a, s) => a + s.ytd, 0))}</td></tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompensationTrackerTab;
